import { useNavigate } from 'react-router-dom'
import { useSetAtom } from 'jotai'
import { PanelLeft, HelpCircle, ShoppingBag } from 'lucide-react'
import { cn } from '@/shared/utils/cn'
import { UserMenu, NotificationBell } from '@/shared/components'
import gdbLogo from '@/assets/shared/icons/gdb-logo.webp'
import { chromeOverlayAtom } from '@/lib/stores/chromeOverlayAtoms'

interface HeaderBarProps {
  onToggleSidebar?: () => void
  className?: string
}

export function HeaderBar({ onToggleSidebar, className }: HeaderBarProps) {
  const navigate = useNavigate()
  const setChromeOverlay = useSetAtom(chromeOverlayAtom)

  const handleOpenHelp = () => {
    // Open the global contextual Help overlay (ChromeOverlays). Help is global
    // chrome now, not a toolbox panel — content switches by current module.
    setChromeOverlay('help')
    console.log('[HeaderBar] Opened contextual help overlay')
  }

  return (
    <header
      className={cn(
        'flex h-10 items-center justify-between border-b border-ue-border-medium bg-ue-bg-darker px-2 shrink-0',
        className
      )}
    >
      {/* Left: Sidebar toggle + Logo */}
      <div className="flex items-center gap-1.5">
        {onToggleSidebar && (
          <button
            type="button"
            onClick={onToggleSidebar}
            className="flex items-center justify-center w-8 h-8 rounded hover:bg-ue-bg-hover transition-colors text-ue-text-tertiary hover:text-ue-text-secondary cursor-pointer"
            aria-label="Toggle sidebar"
          >
            <PanelLeft className="size-4" />
          </button>
        )}
        <button
          type="button"
          className="flex items-center justify-center w-8 h-8 rounded hover:bg-ue-bg-hover transition-colors cursor-pointer"
          onClick={() => navigate('/')}
          title="Go to Dashboard"
        >
          <img
            src={gdbLogo}
            alt="GDB"
            className="w-6 h-6 object-contain"
          />
        </button>
      </div>

      {/* Right: Store, Help, Notifications, User */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => navigate('/modules')}
          className="flex items-center justify-center w-8 h-8 rounded hover:bg-ue-bg-hover transition-colors text-ue-text-tertiary hover:text-ue-text-secondary cursor-pointer"
          title="Modules"
          aria-label="Browse GameDevBuddy modules"
        >
          <ShoppingBag className="size-4" />
        </button>

        <button
          type="button"
          onClick={handleOpenHelp}
          className="flex items-center justify-center w-8 h-8 rounded hover:bg-ue-bg-hover transition-colors text-ue-text-tertiary hover:text-ue-text-secondary cursor-pointer"
          title="Help"
          aria-label="Open contextual help"
        >
          <HelpCircle className="size-4" />
        </button>

        <NotificationBell />

        <UserMenu />
      </div>
    </header>
  )
}
